import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RepositoryItem} from '@github-ui/repos-list-shared'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Spinner} from '@primer/react'
import {type ComponentProps, useRef} from 'react'

import styles from './ReposList.module.css'
import {ReposListItem} from './ReposListItem'
import {SortingDropdown} from './SortingDropdown'

export type ListViewVariant = ComponentProps<typeof ListView>['variant']

export function ReposList({
  compactMode,
  showSpinner,
  repositoryCount,
  repos,
  sortingItemSelected,
  onSortingItemSelect,
  noReposMessage,
}: {
  compactMode: boolean
  showSpinner: boolean
  repositoryCount: number
  repos: RepositoryItem[]
  sortingItemSelected?: string
  onSortingItemSelect: (key: string) => void
  noReposMessage: JSX.Element
}) {
  const savedCompactModeRef = useRef(compactMode)
  function updateCompactModeSetting(value: boolean) {
    if (savedCompactModeRef.current === value) {
      return
    }

    savedCompactModeRef.current = value

    const formData = new FormData()
    formData.set('orgs_repos_compact_mode', value.toString())
    verifiedFetch('/repos/preferences', {method: 'PUT', body: formData})
  }

  const metadata = (
    <ListViewMetadata
      title={<span className={styles.ReposCountText}>{getDisplayReposCount(repositoryCount)}</span>}
      densityToggle={<ListViewDensityToggle />}
    >
      {showSpinner && <Spinner size="small" />}
      <SortingDropdown sortingItemSelected={sortingItemSelected || ''} onSortingItemSelect={onSortingItemSelect} />
    </ListViewMetadata>
  )

  return (
    <div className={styles.ReposListContainer}>
      <ListView
        onVariantChange={value => updateCompactModeSetting(value === 'compact')}
        variant={compactMode ? 'compact' : 'default'}
        metadata={metadata}
        title="Repositories list"
        titleHeaderTag="h2"
      >
        {repos.length > 0 ? (
          repos.map(repo => <ReposListItem key={repo.name} repo={repo} />)
        ) : (
          // A ListView is a <ul> element, so it only accepts <li> children. This component is a workaround to allow
          // Blankslate to be used as a child of a ListView. We don't need to add any additional styles to the <li> element
          // because the ListView already ensures that the <li> element is styled correctly.
          <li>{noReposMessage}</li>
        )}
      </ListView>
    </div>
  )
}

export function getDisplayReposCount(count: number): string {
  const displayCount = human(count, {
    precision: getSignificanceBasedPrecision(count),
  })

  return ` ${displayCount} ${count === 1 ? 'repository' : 'repositories'}`
}

try{ ReposList.displayName ||= 'ReposList' } catch {}